<template>
    <div class="l-goal-details-key-result">
        <div class="row">
            <div class="col-xs-9">
                <div style="font-size: 13px; margin-top: 10px; margin-bottom: 5px">
                    <!-- Icon -->
                    <span v-if="keyResult.type === 'initiative'">
                        <i class="fal fa-sticky-note"></i>
                        <span class="tw-ml-2">{{ $t(`goals.update.labelInitiative`) }}</span>
                    </span>
                    <span v-else>
                        <i class="fal fa-flag"></i>
                        <span class="tw-ml-2">{{ $t(`goals.update.labelKeyResult`) }}</span>
                    </span>
                    <!--/Icon -->

                    <!-- Weight -->
                    <span v-if="keyResult.metricWeight" class="tw-ml-2">
                        {{ keyResult.metricWeight }}%
                    </span>
                    <!--/Weight -->

                    <!-- Owner -->
                    <span v-if="keyResult.ownerUser" class="tw-ml-2">
                        <i class="fal fa-user"></i>
                        <span class="tw-ml-2">
                            {{ keyResult.ownerUser.firstname }}
                            {{ keyResult.ownerUser.lastname }}
                        </span>
                    </span>
                    <span v-else-if="keyResult.ownerTeam" class="tw-ml-2">
                        <i class="fal fa-user"></i>
                        <span class="tw-ml-2">{{ keyResult.ownerTeam.name }}</span>
                    </span>
                    <!--/Owner -->

                    <!-- Deadline -->
                    <span v-if="keyResult.deadline" class="tw-ml-2">
                        <i class="fal fa-calendar"></i>
                        <span class="tw-ml-2">{{ $t(`goals.update.labelDeadline`) }}</span
                        >:
                        {{ keyResult.deadline | moment("MMM DD, YYYY") }}
                    </span>
                    <!--/Deadline -->

                    <!-- Start value -->
                    <span v-if="keyResult.metricStart" class="tw-ml-2">
                        {{ $t("goals.update.startedAt") }}
                        {{ keyResult.metricStart }}
                    </span>
                    <!--/Start value -->

                    <!-- Updates -->
                    <span v-if="goalUpdate" class="tw-ml-2">
                        <span v-show="goalUpdate.change > 0" class="color-success">
                            <i class="fal fa-chart-line"></i>
                            <span class="tw-ml-2">{{ goalUpdate.change }}</span>
                        </span>
                        <span v-show="goalUpdate.change < 0" class="color-warning">
                            <i class="fal fa-chart-line-down"></i>
                            <span class="tw-ml-2">{{ -1 * goalUpdate.change }}</span>
                        </span>
                    </span>
                    <!--/Updates -->

                    <!-- Status -->
                    <span class="btn-group tw-ml-4" style="margin-top: -2px; margin-bottom: 0px">
                        <span>
                            <i
                                :class="{
                                    'fal fa-question-circle': !keyResult.toDiscuss,
                                    'fal fa-flag': keyResult.toDiscuss === 'done',
                                    'fal fa-check': keyResult.toDiscuss === 'onTrack',
                                    'fal fa-exclamation-triangle':
                                        keyResult.toDiscuss === 'inputNeeded',
                                    'fal fa-times': keyResult.toDiscuss === 'offTrack',
                                    'fal fa-stop-circle': keyResult.toDiscuss === 'canceled',
                                }"
                            ></i>
                        </span>
                    </span>
                    <!--/Status -->
                </div>

                <!-- Name -->
                <div
                    style="margin-bottom: 10px;"
                    tabindex="0"
                    @click="toggleKeyResultExpanded"
                    @keyup.enter="toggleKeyResultExpanded"
                >
                    <strong class="tw-cursor-pointer" :class="{ 'tw-line-clamp-1': !isExpanded }">{{
                        keyResult.name
                    }}</strong>
                    <br />
                    <l-text :text="keyResult.description || ''" />
                </div>
                <!--/Name -->
            </div>
            <!-- Metrics -->
            <div class="col-xs-3 col-md-3" style="margin-top: 10px">
                <div v-if="keyResult.metricType === 'none'">
                    <button
                        :disabled="!keyResult.canUpdateKR"
                        class="btn btn-block btn-success btn-raised"
                        @click="() => openEditKRModal(keyResult)"
                    >
                        <i class="fal fa-pencil"></i>
                        <span>{{
                            keyResult.metricCurrent == "100"
                                ? $t(`goals.update.optionComplete`)
                                : $t(`goals.update.optionIncomplete`)
                        }}</span>
                    </button>
                </div>
                <div v-else>
                    <button
                        :disabled="!keyResult.canUpdateKR"
                        class="btn btn-block btn-success btn-raised"
                        @click="() => openEditKRModal(keyResult)"
                    >
                        <i class="fal fa-pencil"></i>
                        <span>{{ metric }} / {{ keyResult.metricEnd }} </span>
                    </button>
                </div>
            </div>
            <!--/Metrics -->
        </div>
        <l-progress
            :progress-percent="keyResult.progress || 0"
            progress-bar-class="progress-bar-info"
            size="xs"
        />
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LProgress from "@/components/LProgress";
import LText from "@/components/LText";

export default {
    components: {
        LProgress,
        LText,
    },
    props: {
        goal: {
            type: Object,
            required: true,
        },
        keyResult: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isExpanded: false,
            isEditing: false,

            metric: null,
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
        }),
        goalUpdate() {
            const latestUpdate = this.goal.updates
                ?.filter(u => u.keyResult === this.keyResult._id)
                ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

            return latestUpdate;
        },
        progressDisabled() {
            return !(
                this.goal?.access?.canUpdateStatus ||
                this.user._id === this.keyResult?.ownerUser?._id ||
                this.user.localTeamMember?.includes(this.keyResult?.ownerTeam?._id)
            );
        },
    },
    watch: {
        keyResult: {
            immediate: true,
            handler(val) {
                this.metric = val.metricCurrent;
            },
        },
    },
    methods: {
        openEditKRModal(keyResult) {
            if (this.progressDisabled) {
                return;
            }
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    editGoalId: this.goal._id,
                    editKeyResultId: keyResult._id,
                },
            });
        },
        toggleKeyResultExpanded(e) {
            e.stopPropagation();
            this.isExpanded = !this.isExpanded;
        },
        updateStatus(status) {
            this.$emit("update", {
                ...this.keyResult,
                toDiscuss: this.keyResult.toDiscuss === status ? "zero" : status,
            });
        },
    },
};
</script>

<style scoped></style>
